import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Trophy, Star, Egg, Sparkles, Gamepad2, PenTool, BookOpen } from "lucide-react";
import { dbHelpers } from "@/lib/database";

const TreasurePage = () => {
  const [achievements, setAchievements] = useState({
    total_stars: 0,
    magic_eggs: 0,
    by_category: {}
  });
  const [totalStars, setTotalStars] = useState(0);
  const [, setEggTypes] = useState([]);

  const magicEggs = useMemo(() => [
    { id: 1, type: 'rainbow', emoji: '🌈', name: 'Rainbow Egg', unlockStars: 10 },
    { id: 2, type: 'unicorn', emoji: '🦄', name: 'Unicorn Egg', unlockStars: 25 },
    { id: 3, type: 'star', emoji: '⭐', name: 'Star Egg', unlockStars: 50 },
    { id: 4, type: 'crystal', emoji: '💎', name: 'Crystal Egg', unlockStars: 75 },
    { id: 5, type: 'dragon', emoji: '🐉', name: 'Dragon Egg', unlockStars: 100 },
    { id: 6, type: 'moon', emoji: '🌙', name: 'Moon Egg', unlockStars: 150 },
    { id: 7, type: 'golden', emoji: '✨', name: 'Golden Egg', unlockStars: 200 }
  ], []);

  const calculateUnlockedEggs = useCallback((stars) => {
    const unlocked = magicEggs.filter(egg => stars >= egg.unlockStars);
    setEggTypes(unlocked);
  }, [magicEggs]);

  const loadAchievements = useCallback(async () => {
    try {
      // Use offline storage
      const summary = await dbHelpers.getAchievementsSummary();
      setAchievements(summary);
      setTotalStars(summary.total_stars);
      calculateUnlockedEggs(summary.total_stars);
    } catch (error) {
      console.error('Failed to load achievements:', error);
    }
  }, [calculateUnlockedEggs]);



  const categories = [
    { key: 'gaming', label: 'Games', icon: Gamepad2, color: 'text-accent' },
    { key: 'crafts', label: 'Crafts', icon: PenTool, color: 'text-secondary' },
    { key: 'reading', label: 'Reading', icon: BookOpen, color: 'text-magic' }
  ];

  useEffect(() => {
    if (typeof loadAchievements === 'function') {
      loadAchievements();
    }
  }, [loadAchievements]);

  const getNextEgg = () => {
    const nextEgg = magicEggs.find(egg => totalStars < egg.unlockStars);
    return nextEgg;
  };

  const nextEgg = getNextEgg();
  const progressToNext = nextEgg ? (totalStars / nextEgg.unlockStars) * 100 : 100;

  return (
    <div className="min-h-screen px-6 py-8 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="font-fredoka text-5xl md:text-6xl font-bold text-foreground mb-4">
          Treasure Shelf 🏆
        </h1>
        <p className="font-nunito text-lg text-muted-foreground">
          Your magical collection of stars and eggs!
        </p>
      </motion.div>

      {/* Total Stars Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="total-stars-card"
        className="max-w-4xl mx-auto mb-12 bg-gradient-to-br from-secondary/20 to-primary/20 border-2 border-border rounded-3xl shadow-hard-xl p-8 text-center"
      >
        <Trophy className="w-20 h-20 text-secondary mx-auto mb-4" />
        <h2 className="font-fredoka text-6xl font-bold text-foreground mb-2">
          {totalStars}
        </h2>
        <p className="font-nunito text-xl text-muted-foreground">
          Total Stars Collected ⭐
        </p>
      </motion.div>

      {/* Progress to Next Egg */}
      {nextEgg && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto mb-12 bg-white border-2 border-border rounded-3xl shadow-hard-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-fredoka text-2xl font-bold text-foreground">
                Next Magic Egg
              </h3>
              <p className="font-nunito text-muted-foreground">
                {nextEgg.name} {nextEgg.emoji}
              </p>
            </div>
            <div className="text-right">
              <p className="font-fredoka text-xl font-bold text-foreground">
                {totalStars} / {nextEgg.unlockStars}
              </p>
              <p className="font-nunito text-sm text-muted-foreground">
                {nextEgg.unlockStars - totalStars} stars to go!
              </p>
            </div>
          </div>
          <div className="h-4 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressToNext}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-primary via-secondary to-accent"
            />
          </div>
        </motion.div>
      )}

      {/* Magic Eggs Collection */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-4xl mx-auto mb-12"
      >
        <h2 className="font-fredoka text-3xl font-bold text-foreground mb-6 text-center">
          Magic Eggs Collection 🥚
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {magicEggs.map((egg, index) => {
            const isUnlocked = totalStars >= egg.unlockStars;

            return (
              <motion.div
                key={egg.id}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`egg-${egg.type}`}
                className={`aspect-square bg-white border-2 ${isUnlocked ? 'border-grass' : 'border-border'
                  } rounded-3xl shadow-hard p-4 flex flex-col items-center justify-center ${isUnlocked ? '' : 'opacity-40 grayscale'
                  }`}
              >
                <span className="text-5xl mb-2">{egg.emoji}</span>
                <p className="font-nunito text-xs text-center text-foreground font-semibold">
                  {egg.name}
                </p>
                {isUnlocked && (
                  <Sparkles className="w-4 h-4 text-grass mt-1" />
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Category Breakdown */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="max-w-4xl mx-auto"
      >
        <h2 className="font-fredoka text-3xl font-bold text-foreground mb-6 text-center">
          Stars by Activity
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            const categoryData = achievements.by_category[category.key] || { stars: 0, eggs: 0 };
            const Icon = category.icon;

            return (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                data-testid={`category-${category.key}`}
                className="bg-white border-2 border-border rounded-3xl shadow-hard-lg p-6 hover:shadow-hard-xl transition-all"
              >
                <Icon className={`w-12 h-12 ${category.color} mb-4`} />
                <h3 className="font-fredoka text-2xl font-bold text-foreground mb-2">
                  {category.label}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-secondary fill-secondary" />
                    <span className="font-nunito text-lg text-foreground">
                      <span className="font-fredoka font-bold">{categoryData.stars}</span> stars
                    </span>
                  </div>
                  {categoryData.eggs > 0 && (
                    <div className="flex items-center gap-2">
                      <Egg className="w-5 h-5 text-magic" />
                      <span className="font-nunito text-lg text-foreground">
                        <span className="font-fredoka font-bold">{categoryData.eggs}</span> eggs
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Motivational Message */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="max-w-2xl mx-auto mt-12 text-center"
      >
        <p className="font-patrick text-2xl text-foreground">
          {totalStars === 0 && "Start playing games and making crafts to collect stars! ✨"}
          {totalStars > 0 && totalStars < 50 && "You're doing amazing! Keep collecting stars! 🌟"}
          {totalStars >= 50 && totalStars < 100 && "Wow! You're halfway to the Dragon Egg! 🐉"}
          {totalStars >= 100 && totalStars < 200 && "Incredible collection! Almost to the Golden Egg! 💫"}
          {totalStars >= 200 && "You're a SparkleWorld champion! 🏆✨"}
        </p>
      </motion.div>
    </div>
  );
};

export default TreasurePage;
